/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { MenuItem, Typography, Button, Card, Select, FormControl, Slider, InputLabel, Grid } from "@material-ui/core";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { WEB_CHAT_TOOL_TIPS } from "../../../utility/toolTipMessages";
import { SURVEY_QUESTION_TYPE } from "../../../utility/constants";

const marks = [
	{
		value: 1,
		label: "1",
	},
	{
		value: 2,
		label: "2",
	},
	{
		value: 3,
		label: "3",
	},
	{
		value: 4,
		label: "4",
	},
	{
		value: 5,
		label: "5",
	},
];

/**
 * This component is used on the Chat configuration page to render the various neccesary fields for editing the web chat widgets configuration.
 * @param {Object} selectedConfiguration The currently selected chat configuration to edit.
 * @param {(Object) => void} setSelectedConfiguration Callback method to set the currently selected chat configuration to edit.
 * @param {Array} contactFlows A list of all the contact flows found in AWS Connect.
 * @param {() => void} setFormEdited Callback method to set the form as edited.
 * @param {boolean} formEdited A boolean value of whether or not te values on this form have been edited.
 */
const SurveyFollowUpQuestionCard = ({
	followUpQuestion,
	setFollowUpQuestion,
	error,
	removeFollowUpQuestion,
	questionType,
	formEdited,
	setFormEdited,
}) => {
	const setFollowUpQuestionValue = (valueName, value) => {
		const newFollowUpQuestion = { ...followUpQuestion };
		newFollowUpQuestion[valueName] = value;
		setFollowUpQuestion(newFollowUpQuestion);
	};
	const [rangeValue, setRangeValue] = React.useState(followUpQuestion.range);
	function valuetext(value) {
		return `${value}`;
	}

	React.useEffect(() => {
		setRangeValue(followUpQuestion.range);
	}, [followUpQuestion.range]);

	return (
		<Card elevation={3} className="mt-3">
			<Grid container spacing={3} className="p-3">
				<Grid item xs={9} lg={6}>
					{questionType === SURVEY_QUESTION_TYPE.SCALE ? (
						<>
							<Typography id="range-slider" variant={"caption"} gutterBottom color={error ? "error" : "textSecondary"}>
								Answer Range:
							</Typography>
							<Slider
								value={rangeValue}
								min={1}
								max={5}
								onChange={(_event, newValue) => {
									setRangeValue(newValue);
								}}
								onChangeCommitted={(_event, newValue) => {
									setFollowUpQuestionValue("range", newValue);
								}}
								style={{ color: "#941012" }}
								marks={marks}
								valueLabelDisplay="off"
								aria-labelledby="range-slider"
								getAriaValueText={valuetext}
							/>
						</>
					) : (
						<FormControl className="w-100">
							<InputLabel id="yes-or-no-helper-label">Selected Answer:</InputLabel>
							<Select
								labelId="yes-or-no-helper-label"
								id="yes-or-no-select-helper"
								value={followUpQuestion.binaryAnswer}
								onChange={(event) => {
									setFollowUpQuestionValue("binaryAnswer", event.target.value);
								}}
							>
								<MenuItem value={1}>Yes</MenuItem>
								<MenuItem value={0}>No</MenuItem>
							</Select>
						</FormControl>
					)}
				</Grid>
				<Grid item xs={3} lg={6}>
					<Button className="float-right" onClick={removeFollowUpQuestion} variant={"outlined"}>
						Delete
					</Button>
				</Grid>
				<Grid item xs={12}>
					<FormTextField
						styleObject={{ width: "100%" }}
						label={"Follow Up Question:"}
						formEdited={formEdited}
						required
						errorMessage={"Should contain a value."}
						setEdited={setFormEdited}
						startingValue={followUpQuestion.question || ""}
						setConfigValue={setFollowUpQuestionValue}
						valueName={"question"}
						toolTipMessage={WEB_CHAT_TOOL_TIPS.SURVEY_FOLLOW_UP_QUESTION}
					/>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Typography color={"error"} gutterBottom variant={"caption"}>
							Answer shared with another follow up question
						</Typography>
					</Grid>
				)}
			</Grid>
		</Card>
	);
};

export default SurveyFollowUpQuestionCard;
